import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as moment from 'moment';
import * as React from 'react';
import { ImageFile } from 'react-dropzone';
import { Prompt } from 'react-router';
import { ProfileTabContansts as ui, PHOTO_DATE_FORMAT } from '../../Config/UIConstants';
import { IUser, UserFactType } from '../../Models/IUser';
import { ExitableAlert } from '../../SharedComponents/ExitableAlert';
import { FullContainer } from '../../SharedComponents/FullContainer';
import { IUploadedPhoto } from '../../Stores/UserPhotoStore';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { getRotatedFileFromImage } from '../../util/resizeImage';
import { Exif } from '../AddPhotosPage/AbstractAddPhotosPage';
import { ProfileForm } from './ProfileForm';

interface IProps {
    store?: ApplicationStore;
}

interface IState {
    firstName: string;
    lastName: string;
    email: string;
    nickname?: string;
    location?: string;
    birthday?: string;
    birthPlace?: string;
    profilePhoto: IUploadedPhoto | null;
    profileImageUrl: string | null;
    showSaveConfirmation: boolean;
    hasMadeChanges: boolean;
}

@inject('store')
@observer
export class ProfileTab extends React.Component<IProps, IState> {
    //TODO: We should set a warning if you try to navigate away from this page, the signup information will be lost.
    public state: IState = {
        firstName: '',
        lastName: '',
        email: '',
        nickname: '',
        location: '',
        birthday: '',
        birthPlace: '',
        showSaveConfirmation: false,
        hasMadeChanges: false,
        profileImageUrl: null,
        profilePhoto: null
    };

    public componentDidMount(): void {
        when(
            () => !!this.props.store!.userProfileStore && !!this.props.store!.userProfileStore.user,
            () => {
                const { user } = this.props.store!.userProfileStore;

                const nickname = this.matchAndStringify(user, UserFactType.Nickname);
                const location = this.matchAndStringify(user, UserFactType.CurrentPlace);
                const birthday = this.matchAndStringify(user, UserFactType.BirthDate);
                const birthPlace = this.matchAndStringify(user, UserFactType.BirthPlace);

                this.setState({
                    firstName: user.first_name || '',
                    lastName: user.last_name || '',
                    email: user.email || '',
                    nickname,
                    location,
                    birthday,
                    birthPlace,
                    profileImageUrl: user.profile_image_url,
                    profilePhoto: null
                });
            }
        );
    }

    private matchAndStringify(user: IUser, type: UserFactType): string | undefined {
        const x = user.facts.find(fact => fact.title === type);
        if (!x) {
            return undefined;
        }
        if (typeof x.value === 'string') {
            return x.value;
        }
        return moment(x.value).format(PHOTO_DATE_FORMAT);
    }

    private handleAddPhoto = async (files: Array<ImageFile>): Promise<void> => {
        const photos = await Exif.getUpdatedImageFiles(files);
        this.setState({ profilePhoto: photos[files.length - 1], hasMadeChanges: true });
    }

    private handleClickSave = async () => {
        this.setState({ showSaveConfirmation: true });

        const { userFamilyStore, userProfileStore } = this.props.store!;

        const { firstName, lastName, email, nickname, location, birthday, birthPlace, profilePhoto, profileImageUrl } = this.state;
        const facts: Array<any> = [];
        if (nickname) {
            facts.push({ title: UserFactType.Nickname, value: nickname });
        }
        if (location) {
            facts.push({ title: UserFactType.CurrentPlace, value: location });
        }
        if (birthday) {
            facts.push({ title: UserFactType.BirthDate, value: birthday });
        }
        if (birthPlace) {
            facts.push({ title: UserFactType.BirthPlace, value: birthPlace });
        }
        let photoToUpload = profilePhoto ? await getRotatedFileFromImage(profilePhoto.image.preview, profilePhoto.rotateDegrees) : null;
        const shouldRemovePhoto = profilePhoto === undefined && profileImageUrl === undefined;
        await userProfileStore.updateUserProfile({ first_name: firstName, last_name: lastName, email, facts }, photoToUpload, shouldRemovePhoto);
        await userFamilyStore.loadFamilies();
        this.setState({ hasMadeChanges: false });
    }

    public render() {
        const { profilePhoto, profileImageUrl, ...userProfile } = this.state;
        return this.props.store!.storeIsReady && this.props.store!.userProfileStore.user ? (
            <FullContainer>
                <Prompt when={this.state.hasMadeChanges} message={ui.UNSAVED_PROMPT} />
                <ProfileForm
                    profile={userProfile}
                    profilePhoto={profilePhoto || profileImageUrl || undefined}
                    onChangeProfile={profile => this.setState({ ...profile, hasMadeChanges: true })}
                    onAddPhoto={this.handleAddPhoto}
                    onRemovePhoto={() => this.setState({ profileImageUrl: null, profilePhoto: null, hasMadeChanges: true })}
                    onPressSave={this.handleClickSave}
                    hasMadeChanges={this.state.hasMadeChanges}
                />
                {this.state.showSaveConfirmation && (
                    <ExitableAlert headerText={ui.EXIT_HEADER} confirmText={ui.OK} onConfirmClick={() => this.setState({ showSaveConfirmation: false })} />
                )}
            </FullContainer>
        ) : null;
    }
}



// WEBPACK FOOTER //
// ./src/Pages/ProfileSettings/ProfileTab.tsx